import "./Text.css";
import { smartenChildren } from "@utils/text";
import type { JSX, ParentComponent } from "solid-js";
import { children, Show, splitProps } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Tooltip, TooltipTrigger } from "../Tooltip";

export type TextColor =
	| "ink"
	| "white"
	| "fg1"
	| "fg2"
	| "fg3"
	| "fg4"
	| "fg5"
	| "primary"
	| "error"
	| "warning"
	| "success"
	| "blue"
	| "purple"
	| "inherit";

export type TextSize =
	| "xsmall"
	| "small"
	| "standard"
	| "intermediate"
	| "medium-large"
	| "large"
	| "xlarge";

export type TextWeight = "regular" | "semi-bold" | "bold" | "extra-bold";

/** Maps to CSS `text-transform` */
export type TextTransform = "none" | "capitalize" | "uppercase" | "lowercase";

type Props = {
	as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div" | "span" | "p" | "a";
	class?: string;
	classList?: Record<string, boolean>;
	style?: JSX.CSSProperties;
	title?: string;
	/**
	 * Maps to `--font-size-*` in shared-core.css.
	 * @default "standard"
	 * Rem values are wide (>500px) / narrow (≤500px) viewport.
	 * - xsmall: 0.625rem / 0.75rem
	 * - small: 0.75rem / 0.875rem
	 * - standard: 0.875rem / 0.9375rem
	 * - intermediate: 1rem / 1rem
	 * - medium-large: 1.25rem / 1.0625rem
	 * - large: 1.75rem / 1.25rem
	 * - xlarge: 2.5rem / 1.5rem
	 */
	size?: TextSize;
	/**
	 * Maps to `--font-weight-*` in shared-core.css.
	 * @default "regular"
	 * - regular: 400 (`--font-weight-regular`)
	 * - semi-bold: 500 (`--font-weight-semi-bold`)
	 * - bold: 600 (`--font-weight-bold`)
	 * - extra-bold: 700 (`--font-weight-extra-bold`)
	 */
	weight?: TextWeight;
	/**
	 * Maps to `--colour-fg-*`, `--white`, and status tokens in shared-core.css.
	 * Light / dark pairs use `light-dark()`; brand status colours are fixed.
	 * - white: `--white` — `--source-white` #fefdff / `--source-black` #050505
	 * - fg1: `--colour-fg-1` — `--source-grey-800` #4a5158 / `--source-grey-200` #e9ecef
	 * - fg2: `--colour-fg-2` — `--source-grey-700` #646c74 / `--source-grey-400` #ced4da
	 * - fg3: `--colour-fg-3` — `--source-grey-600` #818991 / `--source-grey-500` #9fa7af
	 * - fg4: `--colour-fg-4` — `--source-grey-500` #9fa7af / `--source-grey-700` #646c74
	 * - fg5: `--colour-fg-5` — `--source-grey-400` #ced4da / `--source-grey-800` #4a5158
	 * - primary: `--gvt-primary` — `--gvt-blue` #0f6bfe (fixed)
	 * - error: `--gvt-error` — `--gvt-error` #de0d2c (fixed)
	 * - warning: `--gvt-warning` — `--gvt-warning` #de7c0d (fixed)
	 * - success: `--gvt-green` — `--gvt-green` #0cb25f (fixed)
	 * - inherit: parent color
	 */
	color?: TextColor;
	transform?: TextTransform;
	center?: boolean;
	/**
	 * Truncates text with an ellipsis if it overflows its container. `"tooltip"` also shows
	 * the full text in a tooltip on hover, only while it is cut off.
	 */
	ellipsis?: boolean | "tooltip";

	/**
	 * Applies typographic smart quotes and em dashes to string children.
	 * Element children (e.g. Link) are preserved.
	 * @default false
	 */
	smarten?: boolean;

	family?: "body" | "mono" | "display";
	href?: string;
	onClick?: (event: MouseEvent) => void;
};

/**
 * A general purpose text component. Should be flexible enough to handle most text needs.
 * Can be composed to create more specific text components.
 */
export const Text: ParentComponent<Props> = (props) => {
	const resolved = children(() => props.children);

	const classList = () => ({
		...props.classList,
		[`gvtui-text--${props.size}`]: props.size !== undefined,
		[`gvtui-text--weight-${props.weight}`]: props.weight !== undefined,
		[`gvtui-text--color-${props.color}`]: props.color !== undefined,
		[`gvtui-text--transform-${props.transform}`]: props.transform !== undefined,
		[`gvtui-text--ellipsis`]:
			props.ellipsis === true || props.ellipsis === "tooltip",
		[`gvtui-text--family-${props.family}`]: props.family !== undefined,
		[`gvtui-text--center`]: props.center === true,
	});

	let element: HTMLElement | undefined;

	const text = () => (
		<Dynamic
			ref={element}
			component={props.as ?? "div"}
			class={`gvtui-text ${props.class ?? ""}`}
			classList={classList()}
			style={props.style}
			title={props.title}
			href={props.href}
			onClick={props.onClick}
		>
			{props.smarten === true
				? (smartenChildren(resolved()) as JSX.Element)
				: resolved()}
		</Dynamic>
	);

	const isInteractive = () =>
		props.href !== undefined || props.onClick !== undefined;

	return (
		<Show when={props.ellipsis === "tooltip"} fallback={text()}>
			<TooltipTrigger
				class={
					isInteractive()
						? "gvtui-text-truncation-trigger"
						: "gvtui-text-truncation-trigger gvtui-text-truncation-trigger--inert"
				}
				tabIndex={isInteractive() ? undefined : 0}
				onlyWhenTruncated
				// Read from the rendered text rather than handed `children`: those are
				// already mounted in the label, and mounting them again would move them.
				renderContent={() => (
					<Tooltip width="wide">{element?.textContent}</Tooltip>
				)}
			>
				{text()}
			</TooltipTrigger>
		</Show>
	);
};

export type LinkProps = Omit<Props, "as"> & {
	href?: string;
	underlined?: boolean;
	hoverColor?: TextColor;
	hoverUnderlined?: boolean;
	onClick?: (event: MouseEvent) => void;
	target?: HTMLAnchorElement["target"];
	rel?: HTMLAnchorElement["rel"];
};

/**
 * Inline text link with optional underline and hover colour tokens.
 *
 * [Design System →](http://localhost:5174/component/Link)
 */
export const Link: ParentComponent<LinkProps> = (props) => {
	const [local, rest] = splitProps(props, [
		"underlined",
		"hoverColor",
		"hoverUnderlined",
		"onClick",
		"children",
	]);

	return (
		<Text
			as="a"
			classList={{
				"gvtui-text--underlined": local.underlined === true,
				[`gvtui-text--hover-color-${local.hoverColor}`]:
					local.hoverColor !== undefined,
				"gvtui-text--hover-underlined": local.hoverUnderlined === true,
			}}
			onClick={local.onClick}
			{...rest}
		>
			{local.children}
		</Text>
	);
};
